import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Input, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Badge } from 'reactstrap';
import { toast } from 'react-toastify';
import { Breadcrumbs, H5 } from '../../../AbstractElements';
import { Edit2, Check, X, Truck, Calendar, DollarSign, FileText, Users, MapPin, CreditCard, MessageSquare, PlusCircle } from 'react-feather';
import { normalizeUppercaseTR, parseCurrencyToFloat, normalizeDecimal, displayDecimal } from '../../../_helper/regexHandler';
import { apiClient } from '../../../Api/client';
import { calculateTevkifat, finalizeCompensation } from '../../../Api/ApiMethods';

const CompensationDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [isFinalizing, setIsFinalizing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [expenses, setExpenses] = useState([]);
    const [insuranceCompany, setInsuranceCompany] = useState('-');
    const [icraCalc, setIcraCalc] = useState(null);
    const [ilamCalc, setIlamCalc] = useState(null);

    // Inline edit state
    const [editingField, setEditingField] = useState(null);
    const editValueRef = useRef('');
    const editInputRef = useRef(null);

    useEffect(() => {
        if (editingField && editInputRef.current) {
            editInputRef.current.focus();
        }
    }, [editingField]);

    // Note modal state
    const [noteModalOpen, setNoteModalOpen] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [editingNoteIndex, setEditingNoteIndex] = useState(null);
    const [isSavingNote, setIsSavingNote] = useState(false);


    const userRole = (localStorage.getItem('userRole') || '').toUpperCase();
    const hasEditRole = userRole.includes('COMPENSATION OFFICER') || userRole.includes('ADMIN') || userRole.includes('MANAGEMENT') || userRole.includes('COORDINATOR');
    const canEditKapak = hasEditRole && (data === null || data?.canEdit !== false);

    const API_ENDPOINT = process.env.REACT_APP_LACIVERT_API_ENDPOINT;

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const fetchResponse = await apiClient.get(`/FinalCompensation/${id}`);
            if (!fetchResponse.data?.isSuccess) throw new Error('Dosya verisi alınamadı');
            const result = fetchResponse.data.data || fetchResponse.data;
            setData(result);

            const promises = [];

            if (result.expenses && result.expenses.length > 0) {
                if (typeof result.expenses[0] === 'string') {
                    const expensePromises = result.expenses.map(expId =>
                        apiClient.get(`/expense/${expId}`).then(res => res.data?.data || res.data).catch(() => null)
                    );
                    promises.push(Promise.all(expensePromises).then(exps => setExpenses(exps.filter(e => e !== null))));
                } else { setExpenses(result.expenses); }
            } else { setExpenses([]); }

            if (result.isSentenced === true) {
                // Sigorta şirketi kapakta persist ediliyor — ekstra API çağrısına gerek yok
                setInsuranceCompany(result.insuranceCompanyName || '-');

                // İlamlı: her vekalet için ayrı tevkifat hesapla
                setIcraCalc(null);
                setIlamCalc(null);
                const snapshotRates = {
                    kdvRate: result.usedKdvRate,
                    tevkifatRate: result.usedTevkifatRate,
                    tevkifatLimit: result.usedTevkifatLimit,
                };
                if (result.enforcementAttorneyFee != null && result.enforcementAttorneyFee > 0) {
                    promises.push(calculateTevkifat(result.enforcementAttorneyFee, snapshotRates).then(res => { if (res?.isSuccess && res?.data) setIcraCalc(res.data); }).catch(() => {}));
                }
                if (result.judgmentAttorneyFee != null && result.judgmentAttorneyFee > 0) {
                    promises.push(calculateTevkifat(result.judgmentAttorneyFee, snapshotRates).then(res => { if (res?.isSuccess && res?.data) setIlamCalc(res.data); }).catch(() => {}));
                }
            } else {
                setInsuranceCompany('-');
                // İlamsız: sadece icra vekalet hesapla
                setIcraCalc(null);
                setIlamCalc(null);
                const snapshotRates = {
                    kdvRate: result.usedKdvRate,
                    tevkifatRate: result.usedTevkifatRate,
                    tevkifatLimit: result.usedTevkifatLimit,
                };
                if (result.enforcementAttorneyFee != null && result.enforcementAttorneyFee > 0) {
                    promises.push(calculateTevkifat(result.enforcementAttorneyFee, snapshotRates).then(res => { if (res?.isSuccess && res?.data) setIcraCalc(res.data); }).catch(() => {}));
                }
            }

            await Promise.all(promises);
        } catch { toast.error('Veri yüklenirken hata oluştu.'); }
        finally { setLoading(false); }
    }, [id, API_ENDPOINT]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const formatCurrency = (v) => v == null ? '₺0,00' : new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(v);
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '-';
    const toEditFormat = (v) => v == null || v === 0 ? '' : String(v).replace('.', ',');

    // ─── Inline Edit ───

    const startEdit = (key, currentValue, type = 'main', expenseId = null) => {
        if (!canEditKapak) return;
        setEditingField({ key, type, id: expenseId });
        editValueRef.current = toEditFormat(currentValue);
    };

    const cancelEdit = () => setEditingField(null);

    const saveInlineEdit = async () => {
        if (!editingField) return;
        const newAmount = parseCurrencyToFloat(editValueRef.current);
        if (isNaN(newAmount)) { toast.error('Geçerli bir sayı girin.'); return; }
        try {
            if (editingField.type === 'main') {
                const payload = {
                    acceptedAmount: data.acceptedAmount, enforcementAttorneyFee: data.enforcementAttorneyFee,
                    judgmentAttorneyFee: data.judgmentAttorneyFee, interestAmount: data.interestAmount,
                    additionalExpenseAmount: data.additionalExpenseAmount,
                    comments: (data.comments || []).map(c => ({ commentType: c.commentType || '', comment: c.comment || '' })),
                    [editingField.key]: newAmount,
                    skipStateChange: true,
                };
                await apiClient.patch(`/FinalCompensation/patch-compensation-info/${id}`, payload);
            } else if (editingField.type === 'expense') {
                const exp = expenses.find(e => e.id === editingField.id);
                if (!exp) throw new Error('Masraf bulunamadı');
                await apiClient.post('/expense', {
                    ...exp, amount: newAmount,
                    associatedBank: exp.associatedBank ? (Array.isArray(exp.associatedBank) ? exp.associatedBank : [exp.associatedBank.id]) : [],
                    accidentFile: Array.isArray(exp.accidentFile) ? exp.accidentFile : [],
                    customer: Array.isArray(exp.customer) ? exp.customer : [],
                });
            }
            toast.success('Güncellendi');
            setEditingField(null);
            fetchData();
        } catch { toast.error('Güncelleme sırasında hata oluştu.'); }
    };

    const handleKeyDown = (e) => { if (e.key === 'Enter') saveInlineEdit(); if (e.key === 'Escape') cancelEdit(); };

    // ─── Diğer Handler'lar ───

    const handleToggleDeduction = async (expense) => {
        if (!canEditKapak) return;
        try { await apiClient.patch(`/expense/patch-is-deducted/${expense.id}`, !expense.isDeducted); toast.success('Düşüm durumu güncellendi'); fetchData(); }
        catch { toast.error('Düşüm durumu güncellenirken hata oluştu.'); }
    };

    const handleFinalize = async () => {
        setIsFinalizing(true);
        try { await finalizeCompensation({ id }); navigate(`${process.env.PUBLIC_URL}/todo-list`); }
        catch { } finally { setIsFinalizing(false); }
    };

    const handleDownloadReport = () => {
        const token = localStorage.getItem('accessToken');
        const url = `${API_ENDPOINT}/Report/create-closure-report?finalCompensationId=${id}&accessToken=${token}`;
        window.open(url, '_blank');
    };

    const handleSaveWithoutStateChange = async () => {
        try {
            await apiClient.patch(`/FinalCompensation/patch-compensation-info/${id}`, {
                acceptedAmount: data.acceptedAmount, enforcementAttorneyFee: data.enforcementAttorneyFee,
                judgmentAttorneyFee: data.judgmentAttorneyFee, interestAmount: data.interestAmount,
                additionalExpenseAmount: data.additionalExpenseAmount,
                comments: (data.comments || []).map(c => ({ commentType: c.commentType || '', comment: c.comment || '' })),
                skipStateChange: true,
            });
            toast.success('Kapak bilgileri kaydedildi.');
            fetchData();
        } catch { toast.error('Kaydetme sırasında hata oluştu.'); }
    };

    const openAddNoteModal = () => { setEditingNoteIndex(null); setNoteText(''); setNoteModalOpen(true); };
    const openEditNoteModal = (i) => { setEditingNoteIndex(i); setNoteText(data.comments[i]?.comment || ''); setNoteModalOpen(true); };

    const handleSaveNote = async () => {
        if (!noteText.trim()) { toast.error('Not boş olamaz.'); return; }
        setIsSavingNote(true);
        try {
            const allComments = (data.comments || []).map((c, i) => ({ commentType: c.commentType || 'Genel', comment: i === editingNoteIndex ? noteText.trim() : (c.comment || '') }));
            if (editingNoteIndex === null) allComments.push({ commentType: 'Genel', comment: noteText.trim() });
            await apiClient.patch(`/FinalCompensation/patch-compensation-info/${id}`, { comments: allComments, skipStateChange: true });
            toast.success(editingNoteIndex !== null ? 'Not güncellendi' : 'Not eklendi');
            setNoteModalOpen(false);
            fetchData();
        } catch { toast.error('Not kaydedilirken hata oluştu.'); }
        finally { setIsSavingNote(false); }
    };

    if (loading) return <div className="p-5 text-center"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Yükleniyor...</span></div></div>;
    if (!data) return <div className="p-5 text-center text-muted">Veri bulunamadı.</div>;

    const isSentenced = data.isSentenced === true;
    const paymentCollection = (() => { const c = data.paymentCollections; if (!c) return null; if (Array.isArray(c)) return c[0] || null; return typeof c === 'object' ? c : null; })();
    const collectedAmount = paymentCollection?.amount || data.collectedAmount || 0;
    const paymentDateValue = data.paymentDate || paymentCollection?.date;
    const getExpenseObj = (type) => { const n = normalizeUppercaseTR(type.trim()); return expenses.find(e => { let t = e.expenseType; if (t && typeof t === 'object') t = t.title || t.name || ''; return t && normalizeUppercaseTR(String(t).trim()) === n; }); };
    const getExpenseAmount = (type) => { const e = getExpenseObj(type); return e ? e.amount : 0; };
    const isExpenseUsedElsewhere = (exp) => exp?.usedInFinalCompensation ? exp.usedInFinalCompensation.some(c => c !== id) : false;

    // Tevkifat düşülmüş net vekalet ücretlerini kullan; hesaplama henüz tamamlanmadıysa brüt değere düş
    const enforcementFeeNet = icraCalc?.kabulEdilenTutar != null ? icraCalc.kabulEdilenTutar : (data.enforcementAttorneyFee || 0);
    const judgmentFeeNet = ilamCalc?.kabulEdilenTutar != null ? ilamCalc.kabulEdilenTutar : (data.judgmentAttorneyFee || 0);

    // Müşteri Hakediş Detayı bileşenleri (frontend görsel için; backend'de aynı formül uygulanır)
    const enforcementShareRate = data.enforcementAttorneyFeeShareRate || 0;
    const judgmentShareRate = data.judgmentAttorneyFeeShareRate || 0;
    const payInterestToCustomer = data.payInterestToCustomer === true;

    const enforcementCustomerShare = enforcementFeeNet * enforcementShareRate / 100;
    const judgmentCustomerShare = judgmentFeeNet * judgmentShareRate / 100;
    const interestCustomerShare = payInterestToCustomer ? (data.interestAmount || 0) : 0;
    const deductedExpensesTotal = expenses.filter(e => e.isDeducted).reduce((s, e) => s + (e.amount || 0), 0);

    // Müşteriye Toplam Ödenecek (backend de aynısını hesaplar; backend değeri varsa onu tercih et)
    const computedCustomerTotal = (data.amountToBePayedToCustomer || 0)
        + enforcementCustomerShare
        + (isSentenced ? judgmentCustomerShare : 0)
        + interestCustomerShare
        - deductedExpensesTotal;
    const customerTotalAmount = data.amountToBePayedToCustomerTotal != null ? data.amountToBePayedToCustomerTotal : computedCustomerTotal;

    // Toplam Masraf — ilamlıda tüm masraf kalemleri + faiz; ilamsızda sadece icra'ya uygun olanlar
    // Faiz "Evet" ise müşteriye gideceği için firma masrafı sayılmaz (çift sayımı önlemek için)
    const interestAsExpense = payInterestToCustomer ? 0 : (data.interestAmount || 0);
    const totalExpenses = isSentenced
        ? (
            (getExpenseAmount('KARŞI VEKALET ÖDEMESİ') || 0)
            + (getExpenseAmount('NOTER ÜCRETİ') || 0)
            + (getExpenseAmount('TAHKİM HARCI') || 0)
            + (getExpenseAmount('EKSPER FATURASI') || 0)
            + (getExpenseAmount('ISLAH ÖDEMESİ') || 0)
            + (getExpenseAmount('BARO PULU') || 0)
            + (data.additionalExpenseAmount || 0)
            + (getExpenseAmount('TAKİP AÇILIŞ MASRAFI') || 0)
            + (getExpenseAmount('TAHSİL HARCI') || 0)
            + interestAsExpense
        )
        : (
            (getExpenseAmount('TAKİP AÇILIŞ MASRAFI') || 0)
            + (getExpenseAmount('TAHSİL HARCI') || 0)
            + interestAsExpense
        );
    // Hesap Farkı — kalan firma kazancı (toplam masraf göstermelik, hesap farkından çıkarılmıyor)
    const calculationDifference = collectedAmount - customerTotalAmount;

    // ─── Ortak Bileşenler ───

    const SectionHeader = ({ icon: Icon, title }) => (
        <div className="d-flex align-items-center px-3 py-2 rounded-top border-bottom" style={{ backgroundColor: '#fff', color: '#2c323f' }}>
            {Icon && <Icon size={15} className="me-2" />}
            <span className="fw-bold" style={{ fontSize: '0.95rem', letterSpacing: '0.3px' }}>{title}</span>
        </div>
    );

    const DetailRow = ({ label, value, editKey, editType, editId, currentRawValue, onEdit, className, valueClassName, index }) => {
        const isEditing = editingField && editingField.key === editKey && editingField.type === (editType || 'main');
        const hasInlineEdit = editKey && canEditKapak;
        const hasCustomEdit = onEdit && canEditKapak;

        return (
            <Row className={`border-bottom py-3 px-2 g-0 ${className || ''}`} style={{ backgroundColor: index != null && index % 2 === 1 ? '#f8f9fa' : '#fff' }}>
                <Col xs={6} className="d-flex align-items-center ps-3">
                    <span className="fw-bold" style={{ fontSize: '0.85rem' }}>{label}</span>
                </Col>
                <Col xs={6} className="d-flex align-items-center justify-content-between px-3" style={{ borderLeft: '3px solid #dee2e6' }}>
                    {isEditing ? (
                        <div className="d-flex align-items-center w-100">
                            <Input type="text" defaultValue={editValueRef.current} onChange={(e) => { const normalized = normalizeDecimal(e.target.value); e.target.value = displayDecimal(normalized); editValueRef.current = e.target.value; }} onKeyDown={handleKeyDown} innerRef={editInputRef} style={{ flex: 1, height: '32px', fontSize: '0.85rem' }} placeholder="Tutar girin" />
                            <Check size={18} className="ms-2 text-success" style={{ cursor: 'pointer', flexShrink: 0 }} onClick={saveInlineEdit} />
                            <X size={18} className="ms-1 text-danger" style={{ cursor: 'pointer', flexShrink: 0 }} onClick={cancelEdit} />
                        </div>
                    ) : (
                        <>
                            <span className={`fw-bold ${valueClassName || ''}`} style={{ fontSize: '0.85rem' }}>{value}</span>
                            {hasInlineEdit && (
                                <Edit2 size={13} className="ms-2 text-muted" style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => startEdit(editKey, currentRawValue, editType || 'main', editId)} />
                            )}
                            {hasCustomEdit && !hasInlineEdit && (
                                <Edit2 size={13} className="ms-2 text-muted" style={{ cursor: 'pointer', flexShrink: 0 }} onClick={onEdit} />
                            )}
                        </>
                    )}
                </Col>
            </Row>
        );
    };

    const handleSaveAcceptedAmount = async (expenseId, newValue) => {
        const parsed = parseCurrencyToFloat(newValue);
        if (isNaN(parsed)) { toast.error('Geçerli bir sayı girin.'); return; }
        try {
            await apiClient.patch(`/expense/patch-accepted-amount/${expenseId}`, parsed);
            toast.success('Kabul edilen tutar güncellendi');
            setEditingField(null);
            fetchData();
        } catch { toast.error('Kabul edilen tutar güncellenirken hata oluştu.'); }
    };

    const ExpenseRowWithCheckbox = ({ label, type, className, index }) => {
        const expense = getExpenseObj(type);
        const expAmount = expense ? expense.amount : 0;
        const expAccepted = expense?.acceptedAmount;
        const expId = expense?.id;
        const usedElsewhere = isExpenseUsedElsewhere(expense);
        const fieldKey = `accepted_${type}`;
        const isEditing = editingField && editingField.key === fieldKey && editingField.type === 'accepted';

        return (
            <Row className={`border-bottom py-3 px-2 g-0 ${className || ''}`} style={{ backgroundColor: index != null && index % 2 === 1 ? '#f8f9fa' : '#fff' }}>
                <Col xs={1} className="d-flex align-items-center justify-content-center" style={{ borderRight: '3px solid #dee2e6' }}>
                    {expense && (
                        <Button
                            size="sm"
                            color={expense.isDeducted ? 'danger' : 'success'}
                            outline
                            disabled={!canEditKapak || usedElsewhere}
                            onClick={() => handleToggleDeduction(expense)}
                            title={usedElsewhere ? 'Bu masraf başka bir kapakta kullanıldı' : (expense.isDeducted ? 'Düşümü kaldır' : 'Düşüme dahil et')}
                            style={{ fontSize: '0.75rem', padding: '2px 8px', minWidth: '50px' }}
                        >
                            {expense.isDeducted ? 'Düşme' : 'Düş'}
                        </Button>
                    )}
                </Col>
                <Col xs={4} className="d-flex align-items-center ps-3">
                    <span className="fw-bold" style={{ fontSize: '0.85rem' }}>{label}</span>
                </Col>
                <Col xs={3} className="d-flex align-items-center px-3" style={{ borderLeft: '3px solid #dee2e6' }}>
                    <span className="fw-bold" style={{ fontSize: '0.85rem' }}>{formatCurrency(expAmount)}</span>
                </Col>
                <Col xs={4} className="d-flex align-items-center justify-content-between px-3" style={{ borderLeft: '3px solid #dee2e6' }}>
                    {isEditing ? (
                        <div className="d-flex align-items-center w-100">
                            <Input type="text" defaultValue={editValueRef.current} onChange={(e) => { const normalized = normalizeDecimal(e.target.value); e.target.value = displayDecimal(normalized); editValueRef.current = e.target.value; }} onKeyDown={(e) => { if (e.key === 'Enter') handleSaveAcceptedAmount(expId, editValueRef.current); if (e.key === 'Escape') cancelEdit(); }} innerRef={editInputRef} style={{ flex: 1, height: '32px', fontSize: '0.85rem' }} placeholder="Kabul tutar" />
                            <Check size={18} className="ms-2 text-success" style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => handleSaveAcceptedAmount(expId, editValueRef.current)} />
                            <X size={18} className="ms-1 text-danger" style={{ cursor: 'pointer', flexShrink: 0 }} onClick={cancelEdit} />
                        </div>
                    ) : (
                        <>
                            <span className="fw-bold text-info" style={{ fontSize: '0.85rem' }}>{expAccepted != null ? formatCurrency(expAccepted) : '-'}</span>
                            {expId && canEditKapak && <Edit2 size={13} className="ms-2 text-muted" style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => { setEditingField({ key: fieldKey, type: 'accepted', id: expId }); editValueRef.current = toEditFormat(expAccepted || expAmount); }} />}
                        </>
                    )}
                </Col>
            </Row>
        );
    };

    const SectionCard = ({ children, className }) => (
        <div className={`border rounded shadow-sm mb-3 ${className || ''}`}>{children}</div>
    );

    const VekaletRow = ({ label, editKey, currentRawValue, calcResult, className, index }) => {
        const isEditing = editingField && editingField.key === editKey && editingField.type === 'main';

        return (
            <Row className={`border-bottom py-3 px-2 g-0 ${className || ''}`} style={{ backgroundColor: index != null && index % 2 === 1 ? '#f8f9fa' : '#fff' }}>
                <Col xs={4} className="d-flex align-items-center ps-3">
                    <span className="fw-bold" style={{ fontSize: '0.85rem' }}>{label}</span>
                </Col>
                <Col xs={4} className="d-flex align-items-center px-3" style={{ borderLeft: '3px solid #dee2e6' }}>
                    <span className="fw-bold text-success" style={{ fontSize: '0.85rem' }}>
                        {calcResult ? formatCurrency(calcResult.kabulEdilenTutar) : '-'}
                    </span>
                </Col>
                <Col xs={4} className="d-flex align-items-center justify-content-between px-3" style={{ borderLeft: '3px solid #dee2e6' }}>
                    {isEditing ? (
                        <div className="d-flex align-items-center w-100">
                            <Input type="text" defaultValue={editValueRef.current} onChange={(e) => { const normalized = normalizeDecimal(e.target.value); e.target.value = displayDecimal(normalized); editValueRef.current = e.target.value; }} onKeyDown={handleKeyDown} innerRef={editInputRef} style={{ flex: 1, height: '32px', fontSize: '0.85rem' }} placeholder="Tutar girin" />
                            <Check size={18} className="ms-2 text-success" style={{ cursor: 'pointer', flexShrink: 0 }} onClick={saveInlineEdit} />
                            <X size={18} className="ms-1 text-danger" style={{ cursor: 'pointer', flexShrink: 0 }} onClick={cancelEdit} />
                        </div>
                    ) : (
                        <>
                            <span className="fw-bold text-danger" style={{ fontSize: '0.85rem' }}>{formatCurrency(currentRawValue)}</span>
                            {canEditKapak && (
                                <Edit2 size={13} className="ms-2 text-muted" style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => startEdit(editKey, currentRawValue)} />
                            )}
                        </>
                    )}
                </Col>
            </Row>
        );
    };

    // ─── İLAMSIZ LAYOUT ───

    const IlamsizLayout = () => (
        <>
            <CardHeader className="text-center py-3" style={{ backgroundColor: '#f8f9fa', borderBottom: 'none' }}>
                <div className="d-flex justify-content-center align-items-center">
                    <H5 className="mb-0">Takip Detay Türü</H5>
                    <Badge color="warning" className="ms-2" style={{ borderRadius: '4px' }}>İlamsız</Badge>
                </div>
                <div className="mt-2 fw-bold fs-5 text-dark">
                    {Array.isArray(data.trackingDetailType) && data.trackingDetailType.length > 0
                        ? data.trackingDetailType.map(t => typeof t === 'object' ? (t.title || t.name || '-') : t).join(', ') : '-'}
                </div>
            </CardHeader>
            <CardBody className="p-4" style={{ backgroundColor: 'transparent' }}>
                <Row>
                    <Col lg={6}>
                        <SectionCard>
                            <SectionHeader icon={Truck} title="Araç Bilgileri" />
                            <DetailRow label="Plaka" value={data.licensePlate || '-'} index={0} />
                            <DetailRow label="Kaza Tarihi" value={formatDate(data.accidentDate)} className="border-bottom-0" index={1} />
                        </SectionCard>
                    </Col>
                    <Col lg={6}>
                        <SectionCard>
                            <SectionHeader icon={Users} title="Taraf Bilgileri" />
                            <DetailRow label="Müvekkil" value={data.customerName || '-'} index={0} />
                            {data.debtor && data.debtor.length > 0 ? data.debtor.map((d, i) => (
                                <DetailRow key={i} label={`Borçlu ${i + 1}`} value={d.name || '-'} index={i + 1} />
                            )) : (
                                <DetailRow label="Borçlu 1" value="-" index={1} />
                            )}
                        </SectionCard>
                    </Col>
                </Row>
                <Row>
                    <Col lg={6}>
                        <SectionCard>
                            <SectionHeader icon={Calendar} title="Ödeme Bilgileri" />
                            <DetailRow label="Ödeme Tarihi" value={formatDate(paymentDateValue)} className="border-bottom-0" index={0} />
                        </SectionCard>
                    </Col>
                </Row>

                <Row>
                    <Col lg={6}>
                        <SectionCard>
                            <SectionHeader icon={MapPin} title="İcra Bilgileri" />
                            <DetailRow label="Adliye" value={data.courtOffice || '-'} index={0} />
                            <DetailRow label="Yıl" value={data.year || '-'} index={1} />
                            <DetailRow label="İcra Esas Numarası" value={data.enforcementCaseNumber || '-'} className="border-bottom-0" index={2} />
                        </SectionCard>
                    </Col>
                    <Col lg={6}>
                        <SectionCard>
                            <SectionHeader icon={CreditCard} title="Vekalet Ücretleri" />
                            <Row className="py-3 px-2 g-0 border-bottom" style={{ backgroundColor: '#f8f9fa' }}>
                                <Col xs={4} className="d-flex align-items-center ps-3"><span className="fw-bold" style={{ fontSize: '0.85rem' }}>Vekalet Türü</span></Col>
                                <Col xs={4} className="d-flex align-items-center px-3" style={{ borderLeft: '3px solid #dee2e6' }}><span className="fw-bold" style={{ fontSize: '0.85rem' }}>Hesaplanan Tutar</span></Col>
                                <Col xs={4} className="d-flex align-items-center px-3" style={{ borderLeft: '3px solid #dee2e6' }}><span className="fw-bold" style={{ fontSize: '0.85rem' }}>Girilen Tutar</span></Col>
                            </Row>
                            <VekaletRow label="İcra Vekalet Ücreti" editKey="enforcementAttorneyFee" currentRawValue={data.enforcementAttorneyFee} calcResult={icraCalc} className="border-bottom-0" index={0} />
                        </SectionCard>
                    </Col>
                </Row>

                <SectionCard>
                    <SectionHeader icon={DollarSign} title="Finansal Bilgiler" />
                    <DetailRow label="Talep Edilen Tutar" value={formatCurrency(data.requestedAmount)} index={0} />
                    <DetailRow label="Kabul Edilen Tutar" value={formatCurrency(data.acceptedAmount)} valueClassName="text-danger" editKey="acceptedAmount" currentRawValue={data.acceptedAmount} index={1} />
                    <DetailRow label="Müşteri Sözleşme Oranı" value={`%${data.commisionRate || data.customerContractRate || 0}`} index={2} />
                    <DetailRow label="Müşteri Sözleşme Tutarı" value={formatCurrency(data.commisionFee || data.commissionAmount)} valueClassName="text-success" index={3} />
                    <DetailRow label="Müşteriye Ödenecek Tutar" value={formatCurrency(data.amountToBePayedToCustomer)} valueClassName="text-success" index={4} />
                    <DetailRow label="Gelen Para" value={formatCurrency(collectedAmount)} className="border-bottom-0" index={5} />
                </SectionCard>

                <SectionCard>
                    <SectionHeader icon={Users} title="Müşteri Hakediş Detayı" />
                    <DetailRow label="Müşteriye Ödenecek Tutar" value={formatCurrency(data.amountToBePayedToCustomer)} index={0} />
                    <DetailRow label={`İcra Vekalet Hakediş (%${enforcementShareRate})`} value={`+ ${formatCurrency(enforcementCustomerShare)}`} valueClassName="text-success" index={1} />
                    <DetailRow label={`Faiz (${payInterestToCustomer ? 'Evet' : 'Hayır'})`} value={`+ ${formatCurrency(interestCustomerShare)}`} valueClassName="text-success" index={2} />
                    {/* Düşmeler satırı gizlendi — hesaplamalarda kullanılmaya devam ediyor */}
                </SectionCard>

                <div className="d-flex justify-content-between align-items-center px-3 py-3 border rounded shadow-sm mb-3" style={{ backgroundColor: '#fff9c4' }}>
                    <span className="fw-bold">Müşteriye Ödenecek Toplam Tutar</span>
                    <span className="fw-bold">{formatCurrency(customerTotalAmount)}</span>
                </div>

                <SectionCard>
                    <SectionHeader icon={FileText} title="Masraflar" />
                    <DetailRow label="Takip Masraf Tutarı" value={formatCurrency(getExpenseAmount('TAKİP AÇILIŞ MASRAFI'))} index={0} />
                    <DetailRow label="Tahsil Harcı Tutarı" value={formatCurrency(getExpenseAmount('TAHSİL HARCI'))} index={1} />
                    <DetailRow label="Faiz Tutarı" value={formatCurrency(data.interestAmount)} valueClassName="text-danger" editKey="interestAmount" currentRawValue={data.interestAmount} className="border-bottom-0" index={2} />
                </SectionCard>

                <Row>
                    <Col lg={6}><div className="d-flex justify-content-between align-items-center px-3 py-3 border rounded shadow-sm mb-3" style={{ backgroundColor: '#e8f5e9' }}><span className="fw-bold">Toplam Masraf</span><span className="fw-bold">{formatCurrency(totalExpenses)}</span></div></Col>
                    <Col lg={6}><div className="d-flex justify-content-between align-items-center px-3 py-3 border rounded shadow-sm mb-3" style={{ backgroundColor: '#e3f2fd' }}><span className="fw-bold">Hesap Farkı</span><span className="fw-bold">{formatCurrency(calculationDifference)}</span></div></Col>
                </Row>

                <SectionCard>
                    <SectionHeader icon={MessageSquare} title="Notlar" />
                    {data.comments && data.comments.length > 0 && data.comments.map((comment, i) => (
                        <DetailRow key={i} label={`NOT ${i + 1}`} value={comment.comment} valueClassName="text-danger" onEdit={canEditKapak ? () => openEditNoteModal(i) : undefined} index={i} />
                    ))}
                    {canEditKapak && (
                        <div className="p-2 text-center">
                            <Button color="outline-primary" size="sm" className="d-inline-flex align-items-center" onClick={openAddNoteModal}>
                                <PlusCircle size={14} className="me-1" /> Not Ekle
                            </Button>
                        </div>
                    )}
                </SectionCard>

                {canEditKapak && data.compensationState?.title === 'Kapak İşlemleri Bekleniyor' && (
                    <div className="d-flex justify-content-end mb-3">
                        <Button color="success" size="lg" onClick={handleFinalize} disabled={isFinalizing}>
                            {isFinalizing ? 'Kapak Oluşturuluyor...' : 'Kapak Yap'}
                        </Button>
                    </div>
                )}

                {data.compensationState?.order === 3 && (
                    <div className="d-flex justify-content-end mb-3">
                        <Button color="info" size="lg" className="me-2" onClick={handleDownloadReport}>
                            Rapor İndir
                        </Button>
                        {canEditKapak && (
                            <Button color="primary" size="lg" onClick={handleSaveWithoutStateChange}>
                                Kaydet
                            </Button>
                        )}
                    </div>
                )}
            </CardBody>
        </>
    );

    // ─── İLAMLI LAYOUT ───

    const IlamliLayout = () => (
        <>
            <CardHeader className="text-center py-3" style={{ backgroundColor: '#f8f9fa', borderBottom: 'none' }}>
                <div className="d-flex justify-content-center align-items-center">
                    <H5 className="mb-0">Dosya Türü</H5>
                    <Badge color="primary" className="ms-2" style={{ borderRadius: '4px' }}>İlamlı</Badge>
                </div>
                <div className="mt-2 fw-bold fs-5 text-dark">
                    {Array.isArray(data.fileType) ? data.fileType.map(f => f.title || f).join(', ') : (data.fileType?.title || data.fileType || '-')}
                </div>
            </CardHeader>
            <CardBody className="p-4" style={{ backgroundColor: 'transparent' }}>
                <Row>
                    <Col lg={6}>
                        <SectionCard>
                            <SectionHeader icon={Truck} title="Araç Bilgileri" />
                            <DetailRow label="Plaka" value={data.licensePlate || '-'} index={0} />
                            <DetailRow label="Karşı Plaka" value={data.counterpartyLicensePlate || '-'} index={1} />
                            <DetailRow label="Kaza Tarihi" value={formatDate(data.accidentDate)} index={2} />
                            <DetailRow label="Müvekkil Kusur Oranı" value={`%${data.ourFaultRate || 0}`} className="border-bottom-0" index={3} />
                        </SectionCard>
                    </Col>
                    <Col lg={6}>
                        <SectionCard>
                            <SectionHeader icon={Calendar} title="Ödeme Bilgileri" />
                            <DetailRow label="Ödeme Tarihi" value={formatDate(paymentDateValue)} className="border-bottom-0" index={0} />
                        </SectionCard>
                        <SectionCard>
                            <SectionHeader icon={Users} title="Taraf Bilgileri" />
                            <DetailRow label="Müvekkil" value={data.customerName || '-'} index={0} />
                            <DetailRow label="Sigorta Şirketi" value={insuranceCompany} className="border-bottom-0" index={1} />
                        </SectionCard>
                    </Col>
                </Row>

                <Row>
                    <Col lg={6}>
                        <SectionCard>
                            <SectionHeader icon={MapPin} title="Dosya Bilgileri" />
                            <DetailRow label="İcra Esas Numarası" value={data.enforcementCaseNumber || '-'} index={0} />
                            <DetailRow label="Tahkim Esas Numarası" value={data.fileCaseNumber || '-'} className="border-bottom-0" index={1} />
                        </SectionCard>
                    </Col>
                    <Col lg={6}>
                        <SectionCard>
                            <SectionHeader icon={CreditCard} title="Vekalet Ücretleri" />
                            <Row className="py-2 px-2 g-0 border-bottom" style={{ backgroundColor: '#f8f9fa' }}>
                                <Col xs={4} className="d-flex align-items-center ps-3"><span className="fw-bold" style={{ fontSize: '0.85rem' }}>Vekalet Türü</span></Col>
                                <Col xs={4} className="d-flex align-items-center px-3" style={{ borderLeft: '3px solid #dee2e6' }}><span className="fw-bold" style={{ fontSize: '0.85rem' }}>Hesaplanan Tutar</span></Col>
                                <Col xs={4} className="d-flex align-items-center px-3" style={{ borderLeft: '3px solid #dee2e6' }}><span className="fw-bold" style={{ fontSize: '0.85rem' }}>Girilen Tutar</span></Col>
                            </Row>
                            <VekaletRow label="İcra Vekalet Ücreti" editKey="enforcementAttorneyFee" currentRawValue={data.enforcementAttorneyFee} calcResult={icraCalc} index={0} />
                            <VekaletRow label="İlam Vekalet Ücreti" editKey="judgmentAttorneyFee" currentRawValue={data.judgmentAttorneyFee} calcResult={ilamCalc} className="border-bottom-0" index={1} />
                        </SectionCard>
                    </Col>
                </Row>

                <SectionCard>
                    <SectionHeader icon={DollarSign} title="Finansal Bilgiler" />
                    <DetailRow label="Talep Edilen Tutar" value={formatCurrency(data.requestedAmount)} index={0} />
                    <DetailRow label="Kabul Edilen Tutar" value={formatCurrency(data.acceptedAmount)} valueClassName="text-danger" editKey="acceptedAmount" currentRawValue={data.acceptedAmount} index={1} />
                    <DetailRow label="Müşteri Sözleşme Oranı" value={`%${data.commisionRate || data.customerContractRate || 0}`} index={2} />
                    <DetailRow label="Müşteri Sözleşme Tutarı" value={formatCurrency(data.commisionFee || data.commissionAmount)} valueClassName="text-success" index={3} />
                    <DetailRow label="Müşteriye Ödenecek Tutar" value={formatCurrency(data.amountToBePayedToCustomer)} valueClassName="text-success" index={4} />
                    <DetailRow label="Gelen Para" value={formatCurrency(collectedAmount)} className="border-bottom-0" index={5} />
                </SectionCard>

                <SectionCard>
                    <SectionHeader icon={Users} title="Müşteri Hakediş Detayı" />
                    <DetailRow label="Müşteriye Ödenecek Tutar" value={formatCurrency(data.amountToBePayedToCustomer)} index={0} />
                    <DetailRow label={`İcra Vekalet Hakediş (%${enforcementShareRate})`} value={`+ ${formatCurrency(enforcementCustomerShare)}`} valueClassName="text-success" index={1} />
                    <DetailRow label={`İlam Vekalet Hakediş (%${judgmentShareRate})`} value={`+ ${formatCurrency(judgmentCustomerShare)}`} valueClassName="text-success" index={2} />
                    <DetailRow label={`Faiz (${payInterestToCustomer ? 'Evet' : 'Hayır'})`} value={`+ ${formatCurrency(interestCustomerShare)}`} valueClassName="text-success" index={3} />
                    {/* Düşmeler satırı gizlendi — hesaplamalarda kullanılmaya devam ediyor */}
                </SectionCard>

                <div className="d-flex justify-content-between align-items-center px-3 py-3 border rounded shadow-sm mb-3" style={{ backgroundColor: '#fff9c4' }}>
                    <span className="fw-bold">Müşteriye Ödenecek Toplam Tutar</span>
                    <span className="fw-bold">{formatCurrency(customerTotalAmount)}</span>
                </div>

                <SectionCard>
                    <SectionHeader icon={FileText} title="Masraflar" />
                    <Row className="py-3 px-2 g-0 border-bottom" style={{ backgroundColor: '#f8f9fa' }}>
                        <Col xs={1} className="d-flex align-items-center justify-content-center" style={{ borderRight: '3px solid #dee2e6' }}>
                            <span className="fw-bold" style={{ fontSize: '0.85rem' }}>Düş</span>
                        </Col>
                        <Col xs={4} className="d-flex align-items-center ps-3">
                            <span className="fw-bold" style={{ fontSize: '0.85rem' }}>Masraf Kalemi</span>
                        </Col>
                        <Col xs={3} className="d-flex align-items-center px-3" style={{ borderLeft: '3px solid #dee2e6' }}>
                            <span className="fw-bold" style={{ fontSize: '0.85rem' }}>Tutar</span>
                        </Col>
                        <Col xs={4} className="d-flex align-items-center px-3" style={{ borderLeft: '3px solid #dee2e6' }}>
                            <span className="fw-bold" style={{ fontSize: '0.85rem' }}>Kabul Edilen</span>
                        </Col>
                    </Row>
                    <ExpenseRowWithCheckbox label="Karşı Vekalet Ödeme Tutarı" type="KARŞI VEKALET ÖDEMESİ" index={0} />
                    <ExpenseRowWithCheckbox label="Noter Ücreti Tutarı" type="NOTER ÜCRETİ" index={1} />
                    <ExpenseRowWithCheckbox label="Tahkim Harç Tutarı" type="TAHKİM HARCI" index={2} />
                    <ExpenseRowWithCheckbox label="Eksper Ücreti Tutarı" type="EKSPER FATURASI" index={3} />
                    <ExpenseRowWithCheckbox label="Islah Harcı Tutarı" type="ISLAH ÖDEMESİ" index={4} />
                    <ExpenseRowWithCheckbox label="Baro Pulu Tutarı" type="BARO PULU" index={5} />
                    <DetailRow label="Artı Masraf Tutarı" value={formatCurrency(data.additionalExpenseAmount)} valueClassName="text-danger" editKey="additionalExpenseAmount" currentRawValue={data.additionalExpenseAmount} index={6} />
                    <DetailRow label="Takip Masraf Tutarı" value={formatCurrency(getExpenseAmount('TAKİP AÇILIŞ MASRAFI'))} index={7} />
                    <DetailRow label="Tahsil Harcı Tutarı" value={formatCurrency(getExpenseAmount('TAHSİL HARCI'))} index={8} />
                    <DetailRow label="Faiz Tutarı" value={formatCurrency(data.interestAmount)} valueClassName="text-danger" editKey="interestAmount" currentRawValue={data.interestAmount} className="border-bottom-0" index={9} />
                </SectionCard>

                <Row>
                    <Col lg={6}><div className="d-flex justify-content-between align-items-center px-3 py-3 border rounded shadow-sm mb-3" style={{ backgroundColor: '#e8f5e9' }}><span className="fw-bold">Toplam Masraf</span><span className="fw-bold">{formatCurrency(totalExpenses)}</span></div></Col>
                    <Col lg={6}><div className="d-flex justify-content-between align-items-center px-3 py-3 border rounded shadow-sm mb-3" style={{ backgroundColor: '#e3f2fd' }}><span className="fw-bold">Hesap Farkı</span><span className="fw-bold">{formatCurrency(calculationDifference)}</span></div></Col>
                </Row>

                <SectionCard>
                    <SectionHeader icon={MessageSquare} title="Notlar" />
                    {data.comments && data.comments.length > 0 && data.comments.map((comment, i) => (
                        <DetailRow key={i} label={`NOT ${i + 1}`} value={comment.comment} valueClassName="text-danger" onEdit={canEditKapak ? () => openEditNoteModal(i) : undefined} index={i} />
                    ))}
                    {canEditKapak && (
                        <div className="p-2 text-center">
                            <Button color="outline-primary" size="sm" className="d-inline-flex align-items-center" onClick={openAddNoteModal}>
                                <PlusCircle size={14} className="me-1" /> Not Ekle
                            </Button>
                        </div>
                    )}
                </SectionCard>

                {canEditKapak && data.compensationState?.title === 'Kapak İşlemleri Bekleniyor' && (
                    <div className="d-flex justify-content-end mb-3">
                        <Button color="success" size="lg" onClick={handleFinalize} disabled={isFinalizing}>
                            {isFinalizing ? 'Kapak Oluşturuluyor...' : 'Kapak Yap'}
                        </Button>
                    </div>
                )}

                {data.compensationState?.order === 3 && (
                    <div className="d-flex justify-content-end mb-3">
                        <Button color="info" size="lg" className="me-2" onClick={handleDownloadReport}>
                            Rapor İndir
                        </Button>
                        {canEditKapak && (
                            <Button color="primary" size="lg" onClick={handleSaveWithoutStateChange}>
                                Kaydet
                            </Button>
                        )}
                    </div>
                )}
            </CardBody>
        </>
    );

    // ─── ANA RENDER ───

    return (
        <Container fluid={true}>
            <Breadcrumbs parent="Kapak İşlemleri" title="Dosya Detayı" />
            <Row className="justify-content-center">
                <Col xl={11}>
                    <Card style={{ backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }}>
                        {isSentenced ? <IlamliLayout /> : <IlamsizLayout />}
                    </Card>
                </Col>
            </Row>

            <Modal isOpen={noteModalOpen} toggle={() => setNoteModalOpen(false)}>
                <ModalHeader toggle={() => setNoteModalOpen(false)}>
                    {editingNoteIndex !== null ? `NOT ${editingNoteIndex + 1} Düzenle` : 'Yeni Not Ekle'}
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>Not</Label>
                        <Input type="textarea" rows="4" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Notunuzu buraya yazın..." />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setNoteModalOpen(false)}>İptal</Button>
                    <Button color="primary" onClick={handleSaveNote} disabled={isSavingNote}>{isSavingNote ? 'Kaydediliyor...' : 'Kaydet'}</Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
};

export default CompensationDetail;
